import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { updatePaymentStatus } from "../api/payment";
import bookingApi from "../api/booking";
import { openPaymentCheckout } from "../utils/paymentUtils";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const loadScript = (src) => {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const PaymentSuccess = () => {
  const query = useQuery();
  const orderId = query.get("order_id");
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [errorMessage, setErrorMessage] = useState("");
  const [retryData, setRetryData] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!orderId) {
      setStatus("error");
      setErrorMessage("Missing order ID in the URL.");
      return;
    }

    let isMounted = true;
    let pollCount = 0;
    const maxPolls = 6; // Poll up to 6 times (~8-9 seconds)

    const verifyPayment = async () => {
      try {
        const res = await updatePaymentStatus(orderId, "SUCCESS");
        if (!isMounted) return;

        if (res?.success) {
          setStatus("success");
        } else if (res?.isPending && pollCount < maxPolls) {
          pollCount += 1;
          setTimeout(verifyPayment, 1500);
        } else {
          setStatus("error");
          setErrorMessage(res?.message || "Payment verification could not be completed.");
          if (res?.retryData) {
            setRetryData(res.retryData);
          }
        }
      } catch (err) {
        if (!isMounted) return;

        // If gateway is still pending or brief transient network delay, retry before showing error
        const isPendingError =
          err?.isPending ||
          err?.status === 202 ||
          err?.message?.includes("ACTIVE") ||
          err?.message?.includes("PENDING");

        if ((isPendingError || pollCount < 2) && pollCount < maxPolls && !err?.retryData) {
          pollCount += 1;
          setTimeout(verifyPayment, 1500);
        } else {
          setStatus("error");
          setErrorMessage(err.message || "We couldn't verify your payment. Please contact our support team.");
          if (err.retryData) {
            setRetryData(err.retryData);
          }
        }
      }
    };

    verifyPayment();

    return () => {
      isMounted = false;
    };
  }, [orderId]);

  // Automatically redirect to /my-booking after 4 seconds of successful status
  useEffect(() => {
    if (status === "success") {
      const timer = setTimeout(() => {
        navigate("/my-booking");
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [status, navigate]);

  const handleRetryPayment = async () => {
    if (isRetrying) return;
    if (!retryData) return;
    try {
      setIsRetrying(true);
      const res = await bookingApi.retryPayment(retryData);
      if (res.data?.success) {
        const { gatewayOrder } = res.data.data;
        if (!gatewayOrder) {
          alert("Error: Gateway order not returned from backend");
          setIsRetrying(false);
          return;
        }

        const handleReset = () => {
          setIsRetrying(false);
        };

        await openPaymentCheckout(gatewayOrder, handleReset, handleReset);
      } else {
        alert(res.data?.message || "Failed to retry payment.");
        setIsRetrying(false);
      }
    } catch (error) {
      console.error("Retry payment error:", error);
      alert(error.response?.data?.message || "An error occurred while retrying the payment.");
      setIsRetrying(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white px-6 py-12">
      {status === "loading" && (
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-[#FF4D12] border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-lg text-gray-700 font-medium">Verifying payment...</p>
        </div>
      )}

      {status === "success" && (
        <div className="flex flex-col items-center max-w-lg w-full text-center">
          {/* Centered orange circle with custom checkmark */}
          <div className="w-[120px] h-[120px] sm:w-[150px] sm:h-[150px] bg-[#FF4D12] rounded-full flex items-center justify-center mb-8 sm:mb-10 shadow-sm animate-fade-in">
            <svg
              className="w-12 h-12 sm:w-16 sm:h-16 text-white"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>

          {/* Heading */}
          <h2 className="text-[28px] sm:text-[36px] font-bold text-black tracking-tight mb-4 sm:mb-5 leading-tight">
            Booking Successful
          </h2>

          {/* Subtitle */}
          <p className="text-gray-500 text-sm sm:text-base font-normal max-w-sm sm:max-w-md leading-relaxed px-4">
            Your Booking is confirmed and you’ve successfully made payment.
          </p>
        </div>
      )}

      {status === "error" && (
        <div className="flex flex-col items-center max-w-md text-center px-4 animate-fade-in">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <svg
              className="w-10 h-10 text-red-500"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-red-600 mb-3">
            Payment Failed
          </h2>
          <p className="text-gray-500 text-sm sm:text-base leading-relaxed mb-6">
            {errorMessage}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
            {retryData && (
              <button
                onClick={handleRetryPayment}
                disabled={isRetrying}
                className="px-6 py-2.5 rounded-lg bg-[#FF4D12] text-white hover:bg-[#e64415] transition font-semibold disabled:opacity-70 flex justify-center items-center"
              >
                {isRetrying ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Retrying...
                  </>
                ) : (
                  "Retry Payment"
                )}
              </button>
            )}
            <button
              onClick={() => navigate("/my-booking")}
              className="px-6 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition font-semibold"
            >
              Go to My Bookings
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentSuccess;
